<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';

/*
  两张分步表单的接口**直接用 api（@/api/client），不走 eqsApi**：
  当时 eqs.ts 有别人在改，往里加方法一定撞车，而那两个接口只有这一个组件在用。

  附件是另一回事，走 eqsApi（0824）：`moduleAttachments` / `uploadModuleAttachment`
  已经在那儿了，抽屉底下的业务附件区用的就是它们。这里再照着 api.get 写一份，
  等于同一个上传口在系统里有两种写法，将来改鉴权或大小限制会漏掉一处。
*/
import { api } from '@/api/client';
import { eqsApi } from '@/api/eqs';
import { confirmAction, promptAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * 工程变更第 1 步《变更发起申请》/ 第 2 步《变更影响评审》的表单（0823 Tony 手绘稿 + 整理稿）。
 *
 * 选项全部由后端给：变更来源、六大类影响子项是业务口径，
 * 前端另抄一份迟早和服务端对不上（执行清单预览已经吃过这个亏，
 * 所以那边预览和生成共用同一个函数）。这里同理——界面只负责摆和收。
 *
 * 提交 = 勾完这一步的表单任务，**不另起一套推进逻辑**：
 * 推进照旧走上面那颗「推进到第 N 步」按钮和它原来的规矩。
 *
 * 0826 起这张表也是**新建**工程变更的界面（changeId 传 null）：
 * 通用建单表单撤了（和五步重复），新建屏和编辑屏必须长一样（Tony 原话）。
 * 新建态选项来自 changeStepDefinitions（和编辑态同一份口径），
 * 提交动作交给父层（emit 'create'）——建单走哪个接口两个视图各不相同
 * （模块中心 createModuleRecord / 项目页 createProjectResource），这里不该认路。
 */
const props = defineProps<{
  /** null = 新建（表单即建单界面）；有值 = 编辑这条变更 */
  changeId: number | string | null;
  /** 1 = 变更发起申请，2 = 变更影响评审 */
  step: number;
  /** 关联项目下拉的选项（模块中心传全量；项目页不传 = 不显示，建单自动挂当前项目） */
  projectOptions?: Array<{ value: string; label: string }>;
  /** 变更牵头人的候选（项目页传本项目团队；给提示不拦手打） */
  people?: string[];
  /** 新建态：父层正在建单/传附件/提交，按钮要跟着禁用 */
  creating?: boolean;
  /**
   * 只能看不能改（0929）：这张变更挂的项目我不在项目组里，或者账号是只读的。
   * 五步条照后端给的 access.writable 传下来；新建态不看它（建单那条路有自己的门）。
   * 原来这张表谁打开都能填、能交，只读账号也一样，交上去才被后端拒。
   */
  readonly?: boolean;
}>();
const emit = defineEmits<{
  (event: 'submitted'): void;
  /** 新建态点了提交：父层负责 创建 →（选了文件就）传附件 → 提交第 1 步 */
  (event: 'create', payload: {
    values: Record<string, string>;
    file: File | null;
    /** 新建态贴在「客户的变更要求」里的截图，建完单一并上传并标好归属 */
    customerImages?: File[];
  }): void;
}>();

/** 整张表锁成只读：输入框靠外层 fieldset 一次全灰，拖放 / 粘贴传文件在各自的处理函数里拦 */
const locked = computed(() => props.readonly === true && props.changeId !== null);

interface Option { value: string; label: string }

/**
 * 可行性评估的四个涉及项（0825 契约需求 2，《变更管理表》1.4 的表单化）。
 * 勾选值 → normalized.feasibility 键名（mold/tooling/inspection/equipment）
 * 是契约定死的，前后端都不许改写；不做成后端下发，因为它不是选项清单，
 * 而是「哪四个勾会触发这块表单」的固定业务规则。
 */
const FEASIBILITY_ITEMS = [
  { impact: '生产·模具', key: 'mold', label: '模具' },
  { impact: '生产·工装', key: 'tooling', label: '工装' },
  { impact: '质量·检具', key: 'inspection', label: '检具' },
  { impact: '生产·设备', key: 'equipment', label: '设备' },
] as const;
type FeasibilityKey = (typeof FEASIBILITY_ITEMS)[number]['key'];

interface RaiseForm {
  step: number; name: string; submitted: boolean;
  code: string | null; projectNo: string | null;
  /** 这条变更已挂的业务附件数（0826 起文字和附件都必填） */
  attachmentCount?: number;
  sources: string[];
  typeMajors: Option[];
  /** 两个大类各指什么（0826 Tony 给的定义），照抄在选项下面 */
  typeNotes?: Record<string, string>;
  subTypeSuggestions: string[];
  stageGroups: Array<{ name: string; options: string[] }>;
  values: Record<string, string>;
}
interface AssessForm {
  step: number; name: string; submitted: boolean;
  /*
    还差哪几格（0901）。和 submitted 分开看：存量单子是「交过了、但内容不全」
    —— submitted=true 而 gaps 非空。界面照这份念，人不用点了提交被拦一次才知道。
  */
  gaps?: string[];
  groups: Array<{ key: string; name: string; items: Option[] }>;
  riskLevels: string[]; changeLevels: string[];
  /** 可行性责任人的建议（0826：项目团队里唯一命中的模具/工装/检具/设备工程师） */
  feasibilityOwnerSuggestions?: Partial<Record<FeasibilityKey, string | null>>;
  /*
    责任人下拉的选项（0901）。后端只给「派单一定解析得出来」的名字，
    所以从这里选出来的人，点提醒必然发得出去——这是改成下拉的全部意义。
  */
  feasibilityOwnerOptions?: { team: string[]; others: string[] };
  values: {
    impactItems: string[];
    /** 库里存着、但勾选框里画不出来的老写法（通用表单里手打的、导入的） */
    legacyImpactItems?: string[];
    riskLevel: string; changeLevel: string; impactScopes: string;
    /** 0825 需求 2：勾中涉及项的责任人 + 方案提报（0826 又加了预估成本估算） */
    feasibility?: Partial<Record<FeasibilityKey, { owner?: string; note?: string; cost?: string }>>;
    /** 0825 需求 3：老单子没这个字段时后端给空串，界面显示未选、不强迫补 */
    affectsOtherProducts?: '有' | '无' | '';
    affectedProductsPlan?: string;
    affectedProductsCost?: string;
  };
}
interface StepForms { managerNote: string; raise: RaiseForm; assessment: AssessForm }

const forms = ref<StepForms | null>(null);
const busy = ref(false);
const loadFailed = ref(false);

/*
  变更内容的附件（0824 Tony 第 2 条）。

  走的是这条变更**已有的**业务附件接口（module-tools/changes/changes/:id/attachments），
  不另开一张表。0920 起抽屉底下那块通用「业务附件」对变更不再显示——
  这一栏就是这条变更的全量文件清单（没归属的也列在这儿）。
  分两处存的话，人在第 1 步传的凭证在附件区看不见，
  过两周自己都会以为没传成功。
*/
interface ChangeAttachment {
  id: number; fileName: string; sizeBytes: number; uploadedBy?: string;
  /** 后端算好的「我能不能撤」，前端不另判一套 */
  canRemove?: boolean; removeBlockedReason?: string | null;
  /** 归档备注：可行性方案的附件靠「可行性·模具方案」这类备注和第 1 步的凭证区分开 */
  note?: string | null;
}
const attachments = ref<ChangeAttachment[]>([]);
const attachLoading = ref(false);
/** 附件列表这次读成功了没有——读失败时「已挂 N 个附件」退回用 step-forms 带来的数 */
const attachListLoaded = ref(false);
const uploading = ref(false);
const attachInput = ref<HTMLInputElement | null>(null);

async function loadAttachments(): Promise<void> {
  const id = props.changeId;
  if (id === null) return;
  attachLoading.value = true;
  try {
    const payload = await eqsApi.moduleAttachments('changes', 'changes', id);
    attachments.value = (payload.attachments ?? []) as ChangeAttachment[];
    attachListLoaded.value = true;
  } catch {
    // 附件读不到不该让整个第 1 步表单打不开
    attachments.value = [];
    attachListLoaded.value = false;
  } finally {
    attachLoading.value = false;
  }
}

/**
 * 「已挂 N 个附件」的 N（0825 契约需求 1）。
 * 契约让后端在 step-forms 里带 attachmentCount，前端不为了这个数另发请求——
 * 但第 1 步本来就要拉附件列表来显示，列表读到了就以列表长度为准：
 * 人在这张表里刚传完一个附件时，step-forms 里那个数还是旧的，
 * 底下列表 3 条、上面写「已挂 2 个」会让人以为没传上。列表读不到才用契约数兜底。
 */
const attachmentCount = computed(() => (
  attachListLoaded.value ? attachments.value.length : (forms.value?.raise.attachmentCount ?? 0)
));

function attachmentUrl(attachmentId: number): string {
  return eqsApi.moduleAttachmentDownloadUrl('changes', 'changes', props.changeId ?? 0, attachmentId);
}

/** 1024 进制，和抽屉里业务附件那块显示的是同一套口径 */
function formatSize(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

async function uploadAttachment(): Promise<void> {
  const id = props.changeId;
  if (id === null) return;
  const file = attachInput.value?.files?.[0];
  if (!file) { notify('error', '先选个文件', '点「选择文件」挑一个再上传。'); return; }
  if (uploading.value) return;
  uploading.value = true;
  try {
    const payload = new FormData();
    payload.append('file', file);
    await eqsApi.uploadModuleAttachment('changes', 'changes', id, payload);
    notify('success', '附件已上传', file.name);
    if (attachInput.value) attachInput.value.value = '';
    await loadAttachments();
  } catch (caught) {
    notify('error', '上传失败', errorMessage(caught));
  } finally {
    uploading.value = false;
  }
}

/*
  客户要求那一格可以直接贴截图（0828 Tony:「这个做成可贴照片或截图」）。

  客户的变更要求常常本来就是一张图——邮件截图、图纸圈注、微信里发来的照片。
  逼人先存成文件再走「选择文件 → 上传附件」，多数人就干脆只打一行字，
  原文和图都留不下来，而 A 级变更最后要拿这一格跟客户的书面批准对账。

  粘贴和拖拽都接，走的是和「变更内容附件」同一个上传口子——
  不另起一套存储，附件列表里看得到、下载得到、审计查得到。
*/
const pasting = ref(false);
const pasteHint = ref('');

/*
  附件归属靠 note 标记（0911 Tony:「客户的变更需求上传的附件不要放在变更内容一起」）。

  不新开表也不加列：module_record_attachments 上本来就有 note，可行性那几项
  （「可行性·模具方案」）已经在这么用了，跟着同一套走。
  没标记的一律当变更内容附件——存量 18 个附件都是空 note，照旧显示在原来的位置。
*/
const CUSTOMER_ATTACH_NOTE = '客户要求原文';

/** 变更内容的附件：没打客户标记的都算 */
/**
 * 撤下一个附件（0920 Tony：「他们上传的资料不对也删除不掉了」）。
 *
 * 后端是**打标记不是真删**，文件留着可追溯——所以按钮写「撤下」不写「删除」，
 * 别让人以为点一下东西就没了。
 *
 * 撤不掉的时候按钮是灰的，原因直接挂在 title 上（后端算好的 removeBlockedReason），
 * 不做「亮着但点下去被拒」那种——这套系统在审批按钮上栽过一次。
 */
const removingId = ref<number | null>(null);
async function removeAttachment(file: ChangeAttachment): Promise<void> {
  const id = props.changeId;
  if (id === null) return;          // 新建态还没有单子，列表也是空的
  if (file.canRemove === false) return;
  // 应用内弹窗（1031）：原生 prompt 只有一行、标题栏是服务器 IP，钉钉里还可能被拦掉
  const reason = await promptAction({
    title: `撤下「${file.fileName}」？`,
    message: '这份材料不会真的删掉，只是从这条变更上撤下来，需要时管理员还能调出来。',
    prompt: '写一句为什么撤（选填，会记进留痕）',
    confirmLabel: '撤下',
  });
  if (reason === null) return;   // 点了取消
  removingId.value = file.id;
  try {
    await eqsApi.removeModuleAttachment('changes', 'changes', id, file.id, reason);
    notify('success', '附件已撤下', `${file.fileName} 已经从这条变更上撤下。`);
    await loadAttachments();
  } catch (caught) {
    notify('error', '撤不下来', errorMessage(caught));
  } finally {
    removingId.value = null;
  }
}

const contentAttachments = computed(() => attachments.value
  .filter((file) => (file.note ?? '') !== CUSTOMER_ATTACH_NOTE));
/** 客户给的凭证：贴在客户要求那一格里的截图 */
const customerAttachments = computed(() => attachments.value
  .filter((file) => (file.note ?? '') === CUSTOMER_ATTACH_NOTE));

/*
  新建态贴进来 / 选进来的文件（0911 Tony:「不贴附件变更又不能建立」+「Ctrl+V 贴不了附件」）。

  原来单子还没建出来时贴图直接弹「先把这条变更建起来」——而「变更内容附件」
  又是必填，于是新建屏上两个必填条件互相挡着，人只能先存草稿再回来补。
  现在贴的先攥在手里，创建时和选好的文件一起传上去。
*/
const pendingCustomerFiles = ref<File[]>([]);

/** 贴进来的截图没有文件名，给一个能认出来的：客户要求-20260828143012.png */
function pastedName(file: File, index: number): string {
  if (file.name && file.name !== 'image.png' && !/^blob$/iu.test(file.name)) return file.name;
  const stamp = new Date().toISOString().replace(/[-:T]/gu, '').slice(0, 14);
  const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
  return `客户要求-${stamp}${index ? `-${index + 1}` : ''}.${ext}`;
}

async function uploadCustomerFiles(files: File[]): Promise<void> {
  const id = props.changeId;
  if (!files.length || pasting.value) return;
  if (id === null) {
    /*
      单子还没建出来：图先留在手里，创建时一并上传。
      原来这里直接报错「先把这条变更建起来」，但附件又是建单的必填项——
      两个条件互相挡着，新建屏上这条路根本走不通（0911 第 6 页）。
    */
    pendingCustomerFiles.value = [...pendingCustomerFiles.value, ...files];
    pasteHint.value = `已收下 ${pendingCustomerFiles.value.length} 个文件，创建这条变更时一起传上去。`;
    return;
  }
  pasting.value = true;
  pasteHint.value = '';
  try {
    for (const [index, file] of files.entries()) {
      const payload = new FormData();
      payload.append('file', file, pastedName(file, index));
      // 打上归属标记，列表才分得开哪张是客户给的
      payload.append('note', CUSTOMER_ATTACH_NOTE);
      await eqsApi.uploadModuleAttachment('changes', 'changes', id, payload);
    }
    pasteHint.value = `已把 ${files.length} 个文件存成客户要求的附件，就列在这一格下面。`;
    notify('success', files.length > 1 ? `已收 ${files.length} 个客户附件` : '已收下客户附件', '存成了这条变更的附件。');
    await loadAttachments();
  } catch (caught) {
    notify('error', '客户附件没存上', errorMessage(caught));
  } finally {
    pasting.value = false;
  }
}

/**
 * 剪贴板里挑出**文件**；纯文字粘贴不拦，照常进文本框。
 *
 * 0914 Tony 第 7 条:「Ctrl+V 贴不了附件」。原来这里只认 `image/*`——
 * 从资源管理器复制一个 .msg / .pdf / .pptx 再 Ctrl+V，`kind` 是 file 没错，
 * 但 type 不是 image,于是**一声不吭地什么都不发生**。客户的变更要求
 * 十有八九就是一封邮件或一份 PPT,只认图等于这条路只对截图开放。
 */
function filesFrom(list: FileList | null | undefined): File[] {
  return list ? Array.from(list) : [];
}

function onCustomerPaste(event: ClipboardEvent): void {
  if (locked.value) return;
  const files = [...(event.clipboardData?.items ?? [])]
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
  if (!files.length) return;
  event.preventDefault();
  void uploadCustomerFiles(files);
}

const customerDragOver = ref(false);
function onCustomerDragOver(): void { customerDragOver.value = !locked.value; }
function onCustomerDragLeave(): void { customerDragOver.value = false; }

function onCustomerDrop(event: DragEvent): void {
  customerDragOver.value = false;
  // 拖放区是个 div，外层 fieldset 灰不掉它，只读时在这里拦
  if (locked.value) return;
  const files = filesFrom(event.dataTransfer?.files);
  if (!files.length) return;
  event.preventDefault();
  void uploadCustomerFiles(files);
}

/*
  客户要求这一格自己的「选择文件」（0914 Tony 第 7 条:
  「客户的变更需求不能直接上传附件,需要在框内填写信息保存后才能上传附件」）。

  原来这一格只能贴、只能拖,而且只认图。手头是一封 .msg 的人只剩一条路:
  先存草稿 →  回第 1 步 →  用下面「变更内容附件」那个按钮传 —— 传完还归错了地方。
  这里给它一个自己的按钮,新建态也能用（先攥着,建单时一并传）。
*/
const customerFileRef = ref<HTMLInputElement | null>(null);
function pickCustomerFiles(): void {
  const files = filesFrom(customerFileRef.value?.files);
  if (!files.length) return;
  void uploadCustomerFiles(files);
  if (customerFileRef.value) customerFileRef.value.value = '';
}
function dropPendingCustomer(index: number): void {
  pendingCustomerFiles.value = pendingCustomerFiles.value.filter((_, at) => at !== index);
}

/**
 * 第 1 步的可编辑副本。
 * ownerName / projectId 是 0826 搬进来的：通用字段区删掉后，牵头人和「待归属
 * 单子补挂项目」（ECN-项目号-001 编号靠它）没别的地方能填。
 */
const raise = reactive<Record<string, string>>({
  title: '', reason: '', changeSource: '', customerRequirement: '', changeType: '', changeSubType: '',
  occurrenceStage: '', productCategory: '', partNumber: '', productName: '', relatedIssue: '',
  ownerName: '', projectId: '',
  // 客户 CR 号（0826 台账列）：客户发起的变更在这一步顺手登记，选填
  customerChangeNo: '',
});
/** 第 2 步的可编辑副本 */
const picked = ref<string[]>([]);
const riskLevel = ref('');
const changeLevel = ref('');
/*
  可行性评估四项的本地值（0825 需求 2）。
  四项**常驻**在本地、只按勾选决定显示和提交：取消勾选时值不清——
  评审会上勾选反复很常见，一取消就把责任人和方案清掉，等于替人删稿。
*/
const feasibility = reactive<Record<FeasibilityKey, { owner: string; note: string; cost: string }>>({
  mold: { owner: '', note: '', cost: '' },
  tooling: { owner: '', note: '', cost: '' },
  inspection: { owner: '', note: '', cost: '' },
  equipment: { owner: '', note: '', cost: '' },
});
/** 0825 需求 3：是否影响其他产品。空串 = 老单子没填过，界面显示未选 */
const affectsOtherProducts = ref<'有' | '无' | ''>('');
const affectedProductsPlan = ref('');
const affectedProductsCost = ref('');

async function load(): Promise<void> {
  /*
    新建态：还没有单据，选项从五步的静态定义拿（和编辑态同一份口径，后端同一个函数出）。
    definitions 里没有 raise（老后端）就按读失败处理——没有来源/大类选项的表单是残的，
    渲染出来只会让人以为选项被删了。
  */
  if (props.changeId === null) {
    try {
      const payload = await eqsApi.changeStepDefinitions() as unknown as {
        managerNote?: string;
        raise?: Pick<RaiseForm, 'sources' | 'typeMajors' | 'stageGroups' | 'subTypeSuggestions'>;
      };
      if (!payload.raise) throw new Error('后端还没带第 1 步表单选项');
      forms.value = {
        managerNote: payload.managerNote ?? '',
        raise: {
          step: 1, name: '变更发起申请', submitted: false,
          code: null, projectNo: null, attachmentCount: 0,
          sources: payload.raise.sources ?? [],
          typeMajors: payload.raise.typeMajors ?? [],
          subTypeSuggestions: payload.raise.subTypeSuggestions ?? [],
          stageGroups: payload.raise.stageGroups ?? [],
          values: {},
        },
        // 新建态只渲染第 1 步，占位给个空的第 2 步，模板一套逻辑就够
        assessment: {
          step: 2, name: '变更影响评审', submitted: false,
          groups: [], riskLevels: [], changeLevels: [],
          values: { impactItems: [], riskLevel: '', changeLevel: '', impactScopes: '' },
        },
      };
      loadFailed.value = false;
      for (const key of Object.keys(raise)) raise[key] = '';
    } catch {
      forms.value = null;
      loadFailed.value = true;
    }
    return;
  }
  try {
    const payload = await api.get<StepForms>(`module-tools/changes/${props.changeId}/step-forms`);
    forms.value = payload;
    loadFailed.value = false;
    for (const key of Object.keys(raise)) raise[key] = payload.raise.values[key] ?? '';
    const assessValues = payload.assessment.values;
    picked.value = [...(assessValues.impactItems ?? [])];
    riskLevel.value = assessValues.riskLevel ?? '';
    changeLevel.value = assessValues.changeLevel ?? '';
    for (const item of FEASIBILITY_ITEMS) {
      const saved = assessValues.feasibility?.[item.key];
      feasibility[item.key].owner = saved?.owner ?? '';
      feasibility[item.key].note = saved?.note ?? '';
      feasibility[item.key].cost = saved?.cost ?? '';
    }
    // 责任人建议（0826）：填法见 ownerSuggestions 的 watch——只补空格，不动人填的
    ownerSuggestions.value = payload.assessment.feasibilityOwnerSuggestions ?? {};
    affectsOtherProducts.value = assessValues.affectsOtherProducts ?? '';
    affectedProductsPlan.value = assessValues.affectedProductsPlan ?? '';
    affectedProductsCost.value = assessValues.affectedProductsCost ?? '';
  } catch {
    // 读不到就整块不显示：一个区块不该让变更页打不开
    forms.value = null;
    loadFailed.value = true;
  }
}

watch(() => [props.changeId, props.step], () => {
  void load();
  // 第 1 步的凭证区和第 2 步可行性方案的附件用的是同一批业务附件
  void loadAttachments();
}, { immediate: true });

const isRaise = computed(() => props.step === 1);
/** 新建态：这张表就是建单界面（0826，通用建单表单撤了） */
const isCreate = computed(() => props.changeId === null);
const raiseForm = computed(() => forms.value?.raise ?? null);
const assessForm = computed(() => forms.value?.assessment ?? null);
const submitted = computed(() => (isRaise.value ? raiseForm.value?.submitted : assessForm.value?.submitted) ?? false);

/** 新建态的凭证文件；选没选决定创建后要不要自动传附件+提交第 1 步 */
const createFileRef = ref<HTMLInputElement | null>(null);
const createFilePicked = ref(false);
function onCreateFileChange(): void {
  createFilePicked.value = Boolean(createFileRef.value?.files?.length);
}

/*
  变更内容附件也能拖进来（0911 Tony:「所有附件都还不能用直接拖拽的方式上传」）。

  客户要求那一格 0828 就支持拖拽了，这一格一直只有「选择文件」按钮——
  同一个抽屉里两格行为不一样，人试过一次拖不动，就不会再试第二格。
*/
const contentDragOver = ref(false);

function onContentDragOver(): void { contentDragOver.value = !locked.value; }
function onContentDragLeave(): void { contentDragOver.value = false; }

async function onContentDrop(event: DragEvent): Promise<void> {
  contentDragOver.value = false;
  if (locked.value) return;
  const files = [...(event.dataTransfer?.files ?? [])];
  if (!files.length) return;
  const id = props.changeId;
  if (id === null) {
    // 新建态：塞进 file input，走和「选择文件」同一条路，创建时一并上传
    const input = createFileRef.value;
    if (!input) return;
    const bag = new DataTransfer();
    bag.items.add(files[0]);
    input.files = bag.files;
    onCreateFileChange();
    if (files.length > 1) {
      notify('info', '先收下第 1 个', '新建时只能带 1 个附件；其余的建完单在第 1 步接着传。');
    }
    return;
  }
  if (uploading.value) return;
  uploading.value = true;
  try {
    for (const file of files) {
      const payload = new FormData();
      payload.append('file', file);
      await eqsApi.uploadModuleAttachment('changes', 'changes', id, payload);
    }
    notify('success', files.length > 1 ? `已上传 ${files.length} 个附件` : '附件已上传', '在「变更内容附件」里看得到。');
    await loadAttachments();
  } catch (caught) {
    notify('error', '附件没传上', errorMessage(caught));
  } finally {
    uploading.value = false;
  }
}

/**
 * 变更类型：两个大类之外，**把这条单子原来填的值也列出来**。
 * 老单子填的是旧字典里的另外 9 种（存的是主键），
 * 不列出来的话人一打开就看见「没选」——「库里有、界面空白」正是这套系统的老毛病。
 */
const typeOptions = computed<Option[]>(() => {
  const base = raiseForm.value?.typeMajors ?? [];
  const current = raise.changeType;
  if (!current || base.some((option) => option.value === current)) return base;
  const label = raiseForm.value?.values.changeTypeLabel || current;
  return [...base, { value: current, label: `${label}（原有口径）` }];
});

/** 发生阶段同理：老值（P01、T0…）不在两档里也要看得见 */
const stageGroups = computed(() => {
  const base = raiseForm.value?.stageGroups ?? [];
  const current = raise.occurrenceStage;
  if (!current || base.some((group) => group.options.includes(current))) return base;
  return [...base, { name: '原有', options: [current] }];
});

const needsCustomerText = computed(() => raise.changeSource === '客户');

/** 可行性评估只列勾中的涉及项——没勾模具就不该逼人填模具的责任人 */
const feasibilityVisible = computed(
  () => FEASIBILITY_ITEMS.filter((item) => picked.value.includes(item.impact)),
);

/**
 * 可行性责任人的建议（0826：项目团队里唯一命中的对口工程师）。
 * 只在**该项刚勾出来、责任人还空着**时带入——人填过/改过的一个字不动。
 */
const ownerSuggestions = ref<Partial<Record<FeasibilityKey, string | null>>>({});
watch(feasibilityVisible, (items) => {
  for (const item of items) {
    const suggested = ownerSuggestions.value[item.key];
    if (!feasibility[item.key].owner && suggested) feasibility[item.key].owner = suggested;
  }
}, { immediate: true });

/**
 * 勾选互斥（0826 Tony：每类加「不适用」）：
 * 「不适用」= 这一类看过了、确认不涉及——和同类别的具体子项互斥，
 * 勾它就清掉同类别其它勾，勾具体子项就退掉它。跨类别互不影响。
 */
function toggleImpact(groupItems: Option[], value: string): void {
  const index = picked.value.indexOf(value);
  if (index >= 0) {
    picked.value.splice(index, 1);
    return;
  }
  const groupValues = groupItems.map((item) => item.value);
  const notApplicable = groupValues.find((item) => item.endsWith('·不适用'));
  if (value === notApplicable) {
    picked.value = picked.value.filter((item) => !groupValues.includes(item));
  } else if (notApplicable) {
    const naIndex = picked.value.indexOf(notApplicable);
    if (naIndex >= 0) picked.value.splice(naIndex, 1);
  }
  picked.value.push(value);
}

/* ==== 可行性方案的附件（0826 Tony：方案要能传附件，上传按钮做进框里） ==== */
const feasFileInput = ref<HTMLInputElement | null>(null);
const feasUploadKey = ref<FeasibilityKey | null>(null);
const feasUploadingKey = ref<FeasibilityKey | null>(null);

/** 归档判据：同一批业务附件里，靠这个备注认出哪个文件是哪一项的可行性方案 */
function feasibilityNoteOf(label: string): string {
  return `可行性·${label}方案`;
}
function feasibilityFiles(label: string): ChangeAttachment[] {
  return attachments.value.filter((file) => file.note === feasibilityNoteOf(label));
}
function pickFeasibilityFile(key: FeasibilityKey): void {
  feasUploadKey.value = key;
  feasFileInput.value?.click();
}
async function uploadFeasibilityFile(): Promise<void> {
  const key = feasUploadKey.value;
  const file = feasFileInput.value?.files?.[0];
  const id = props.changeId;
  if (!key || !file || id === null || feasUploadingKey.value) return;
  const item = FEASIBILITY_ITEMS.find((entry) => entry.key === key);
  feasUploadingKey.value = key;
  try {
    const payload = new FormData();
    payload.append('file', file);
    payload.append('note', feasibilityNoteOf(item?.label ?? key));
    await eqsApi.uploadModuleAttachment('changes', 'changes', id, payload);
    notify('success', '方案附件已上传', file.name);
    if (feasFileInput.value) feasFileInput.value.value = '';
    await loadAttachments();
  } catch (caught) {
    notify('error', '上传失败', errorMessage(caught));
  } finally {
    feasUploadingKey.value = null;
  }
}

/**
 * 第 1 步的必填项（0826 Tony：内容、附件、客户原文都不许空）。
 * 前端先拦一道给即时反馈；后端 submitRaise 同一套口径再拦一遍，谁也绕不过。
 */
function raiseGaps(): string[] {
  const gaps: string[] = [];
  if (!raise.title.trim()) gaps.push('变更标题');
  if (!raise.reason.trim()) gaps.push('变更内容');
  if (!raise.changeSource) gaps.push('变更来源');
  if (raise.changeSource === '客户' && !raise.customerRequirement.trim()) gaps.push('客户的变更要求（原文）');
  if (!raise.changeType) gaps.push('变更类型');
  /*
    0911 Tony:「变更牵头人一定要填写」。
    这一格原来是选填，于是台账上「谁在推这条变更」是空的——催办催不到人，
    第 3 步要指定变更管理者时也没有依据（下面那段说明里写着"暂由发起人手工指定"）。
    线上 6 条变更里 2 条空着且还在流转，加必填后它们下次提交会被要求补上。
  */
  if (!raise.ownerName.trim()) gaps.push('变更牵头人');
  return gaps;
}

/*
  第 2 步要送的内容。草稿和提交**共用这一份** ——
  两处各拼一次的话，草稿存下去的和提交上去的迟早会长得不一样，
  而这种偏差要等到有人「明明存过草稿，提交时却说没填」才会被发现。

  可行性只送**勾中的**项：取消勾选的项从表单消失，跟着从提交里消失——
  本地留着值只是为了勾回来不用重打，不代表它还算数。
  「影响其他产品」选「无」时把两格清空再送，同理：
  改口成「无」以后还把旧方案存进库，就是「库里有、界面空白」那个老毛病。
*/
function assessmentPayload(): Record<string, unknown> {
  const feasibilityPayload: Partial<Record<FeasibilityKey, { owner: string; note: string; cost: string }>> = {};
  for (const item of feasibilityVisible.value) {
    feasibilityPayload[item.key] = { ...feasibility[item.key] };
  }
  const affectsOther = affectsOtherProducts.value === '有';
  return {
    impactItems: picked.value, riskLevel: riskLevel.value, changeLevel: changeLevel.value,
    feasibility: feasibilityPayload,
    affectsOtherProducts: affectsOtherProducts.value,
    affectedProductsPlan: affectsOther ? affectedProductsPlan.value : '',
    affectedProductsCost: affectsOther ? affectedProductsCost.value : '',
  };
}

/**
 * 存草稿（0901 Tony：这张表不止一个人填）。
 *
 * 存到库里、不是存在浏览器里 —— 存本地的话另一个人打开根本看不见，等于没有草稿。
 * 不判必填、不勾表单任务、不推进；后端回一份「还差什么」，直接念给人听。
 */
async function saveDraft(): Promise<void> {
  if (busy.value || props.creating || props.changeId === null) return;
  busy.value = true;
  try {
    const result = await api.post<{ gaps?: string[] }>(
      `module-tools/changes/${props.changeId}/step-forms/assessment/draft`,
      assessmentPayload(),
    );
    notify(
      'success', '草稿已保存',
      result.gaps?.length
        ? `还差 ${result.gaps.length} 项才能提交，下面红框里列着。`
        : '必填项已经齐了，可以提交。',
    );
    await load();
  } catch (caught) {
    notify('error', '草稿没存上', errorMessage(caught));
  } finally {
    busy.value = false;
  }
}

const ownerOptions = computed(() => assessForm.value?.feasibilityOwnerOptions ?? { team: [], others: [] });

/**
 * 库里存着、但不在下拉名单里的旧责任人（离职了、或改下拉之前手打的）。
 *
 * 必须把它当成一个可选项塞回去 —— 否则 v-model 找不到对应 option，
 * 下拉会显示空白，人一保存就把原来填的人**无声抹掉**了。
 * 这种"换控件顺手丢数据"是最难查的一类回归：界面看着好好的，数据没了。
 */
function legacyOwner(key: FeasibilityKey): string {
  const current = feasibility[key]?.owner?.trim() ?? '';
  if (!current) return '';
  const known = [...ownerOptions.value.team, ...ownerOptions.value.others];
  return known.includes(current) ? '' : current;
}

/** 正在提醒哪一项（按钮转圈用）；空 = 没在发 */
const notifyingKey = ref<FeasibilityKey | null>(null);

/**
 * 提醒某一项的责任人来填方案（0901 Tony：「万一选错了也可以调整，
 * 不然选了就推送有可能搞错」）。
 *
 * **先存草稿再提醒**：后端发给库里存着的那个人，不认输入框里正在打的字。
 * 不这样的话，改了名字没保存就点提醒，信会发给上一个人——
 * 而这正是要按钮的原因，不能在按钮里把同样的坑再挖一遍。
 */
async function notifyOwner(key: FeasibilityKey, label: string): Promise<void> {
  if (busy.value || notifyingKey.value || props.changeId === null) return;
  if (!feasibility[key]?.owner?.trim()) {
    notify('error', '还没填责任人', `先填上${label}的责任人，再提醒他。`);
    return;
  }
  notifyingKey.value = key;
  try {
    await api.post(
      `module-tools/changes/${props.changeId}/step-forms/assessment/draft`,
      assessmentPayload(),
    );
    const result = await api.post<{ notified: boolean; channels: string[]; skipped: string | null; owner: string }>(
      `module-tools/changes/${props.changeId}/step-forms/assessment/notify`,
      { key },
    );
    if (result.notified) {
      const how = result.channels.includes('email') ? '站内信 + 邮件' : '站内信';
      notify('success', `已提醒 ${result.owner}`, `${label}的方案提报已通过${how}发出。`);
    } else {
      // 发不出去要说清楚为什么——按钮点了没反应，人只会以为系统坏了
      notify('info', `没有重复发给 ${result.owner}`, result.skipped ?? '对方还没读上一条提醒。');
    }
    await load();
  } catch (caught) {
    notify('error', '提醒没发出去', errorMessage(caught));
  } finally {
    notifyingKey.value = null;
  }
}

async function submit(): Promise<void> {
  if (busy.value || props.creating) return;
  if (isRaise.value) {
    const gaps = raiseGaps();
    if (gaps.length) {
      notify('error', '必填项还没填全', `${gaps.join('、')}——这几格是第 1 步的硬要求。`);
      return;
    }
  }
  /*
    新建态：建单的接口两个视图各不相同，这里只把填好的值和选好的文件交给父层。
    没选文件也放人建**草稿**（附件常常还在别人手里），但要把话说明白：
    不补附件，第 1 步永远提交不了。
  */
  if (isCreate.value) {
    const file = createFileRef.value?.files?.[0] ?? null;
    if (!file) {
      const ok = await confirmAction({
        title: '还没选附件',
        message: '附件是第 1 步的必填项（方案 PPT / 对比照片 / 图纸截图 / 客户邮件），不传这一步提交不了。'
          + '先把单子建成草稿，之后回第 1 步补传附件再提交？',
        confirmLabel: '先建草稿',
      });
      if (!ok) return;
    }
    emit('create', { values: { ...raise }, file, customerImages: [...pendingCustomerFiles.value] });
    return;
  }
  if (isRaise.value && attachmentCount.value === 0) {
    notify('error', '还没有附件', '至少上传 1 个相关凭证（方案 PPT / 对比照片 / 图纸截图 / 客户邮件），第 1 步才交得上去。');
    return;
  }
  busy.value = true;
  try {
    if (isRaise.value) {
      const result = await api.post<{
        code: string | null; codeCreated: boolean; codeNote: string | null;
        /** 0826：正停在第 1 步时提交完自动推进，这里带回推到了第几步 */
        autoAdvancedTo?: number | null;
      }>(
        `module-tools/changes/${props.changeId}/step-forms/raise`, { ...raise },
      );
      notify(
        'success',
        result.codeCreated ? `已提交，变更编号 ${result.code}` : '变更发起申请已提交',
        [
          result.codeNote,
          result.autoAdvancedTo
            ? `已自动推进到第 ${result.autoAdvancedTo} 步，接着做影响评审。`
            : '这一步的必填项已勾上。',
        ].filter(Boolean).join(' '),
      );
    } else {
      /*
        可行性只送**勾中的**项：取消勾选的项从表单消失，跟着从提交里消失——
        本地留着值只是为了勾回来不用重打，不代表它还算数。
        「影响其他产品」选「无」时把两格清空再送，同理：
        改口成「无」以后还把旧方案存进库，就是「库里有、界面空白」那个老毛病。
      */
      const result = await api.post<{ addedScopes: string[]; autoAdvancedTo?: number | null }>(
        `module-tools/changes/${props.changeId}/step-forms/assessment`,
        assessmentPayload(),
      );
      notify(
        'success', '变更影响评审已提交',
        [
          result.addedScopes?.length
            ? `影响范围补上了：${result.addedScopes.join('、')}（执行清单会照着裁）。`
            : null,
          result.autoAdvancedTo
            ? `已自动推进到第 ${result.autoAdvancedTo} 步，审批链已按路由铺出。`
            : '这一步的必填项已勾上。',
        ].filter(Boolean).join(' '),
      );
    }
    await load();
    emit('submitted');
  } catch (caught) {
    notify('error', '没提交上', errorMessage(caught));
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section v-if="forms" class="change-step-form">
    <p class="form-head">
      <b>{{ isRaise ? '变更发起申请' : '变更影响评审' }}</b>
      <em v-if="isCreate" class="todo">新建 —— 填完提交，这条变更就立起来</em>
      <em v-else-if="submitted" class="ok">已提交</em>
      <em v-else class="todo">还没提交 —— 这一步过不去</em>
    </p>

    <!--
      只能看的时候整张表一次灰掉（0929）：fieldset 的 disabled 会连带里面所有输入框和按钮，
      比逐个加 :disabled 可靠——这张表四十多个控件，漏一个就是一个点了才被拒的口子。
      下载附件的链接不受影响，照样能看。
    -->
    <fieldset class="form-body" :disabled="locked">
    <!-- ============ 第 1 步 ============ -->
    <template v-if="isRaise && raiseForm">
      <label class="field">
        <span>变更标题<b class="req">*</b></span>
        <input v-model="raise.title" type="text" placeholder="带上项目代号，如 X04-C 出风口叶片加强筋">
      </label>


      <div class="field">
        <span>变更来源<b class="req">*</b></span>
        <div class="choices">
          <label v-for="source in raiseForm.sources" :key="source" class="choice">
            <input v-model="raise.changeSource" type="radio" :value="source"><i>{{ source }}</i>
          </label>
        </div>
      </div>
      <!--
        客户发起的必须留原文（程序 7.1「写明客户要求与完成时限」）：
        转述会走样，而 A 级变更最后要拿它跟客户的书面批准对账。
        0905 起它还是 A 级实施前要备案的客户授权（程序 10.2.1.2）——客户发起的单子
        第 3 步不再要「客户批准 / 告知记录」，靠的就是这一格，所以说明里要把这层关系写出来。
      -->
      <div
        v-if="needsCustomerText" class="field customer-req" :class="{ 'is-dragover': customerDragOver }"
        @dragover.prevent="onCustomerDragOver" @dragleave="onCustomerDragLeave" @drop.prevent="onCustomerDrop"
      >
        <label>
          <span>客户的变更要求（原文）<b class="req">*</b></span>
          <textarea
            v-model="raise.customerRequirement" rows="3"
            :class="{ pasting }"
            placeholder="把客户邮件正文 / EWO 内容原样贴进来，不要转述；邮件、PPT、截图可以直接 Ctrl+V 或拖进来"
            @paste="onCustomerPaste"
          ></textarea>
        </label>
        <!--
          0914 Tony 第 7 条：这一格要有自己的「选择文件」。
          原来只能贴、只能拖，而且只认图片——手头是一封 .msg 的人只能先存草稿、
          回头用下面「变更内容附件」那个按钮传，传完还归错了地方。
        -->
        <div class="change-attach-actions">
          <input ref="customerFileRef" type="file" multiple :disabled="pasting" @change="pickCustomerFiles">
          <em class="field-hint">邮件 / PPT / 图纸截图都行，存成客户要求的凭证</em>
        </div>
        <small class="paste-note">
          <template v-if="pasting">正在存客户附件…</template>
          <template v-else-if="pasteHint">{{ pasteHint }}</template>
          <template v-else>也可以直接 Ctrl+V 粘贴或把文件拖进这一块——会存成这条变更的附件，就列在下面。这段原文就是客户发起变更的书面授权，第 3 步不用再填「客户批准 / 告知记录」。</template>
        </small>
        <!-- 客户给的凭证列在客户这一格底下，不混进「变更内容附件」（0911 第 4 页） -->
        <ul v-if="customerAttachments.length" class="change-attach-list customer-attach">
          <li v-for="file in customerAttachments" :key="file.id">
            <a :href="attachmentUrl(file.id)" target="_blank" rel="noopener">{{ file.fileName }}</a>
            <small>{{ formatSize(file.sizeBytes) }}<template v-if="file.uploadedBy"> · {{ file.uploadedBy }}</template></small>
            <button
              class="inline-button attach-remove"
              type="button"
              :disabled="file.canRemove === false || removingId === file.id"
              :title="file.removeBlockedReason || '把这份材料从这条变更上撤下来（不会真的删掉）'"
              @click="removeAttachment(file)"
            >{{ removingId === file.id ? '撤下中…' : '撤下' }}</button>
          </li>
        </ul>
        <!-- 新建态：单子还没建出来，收下的文件先攥在手里，创建时一并传（0911 第 6 页） -->
        <ul v-if="pendingCustomerFiles.length" class="change-attach-list customer-attach">
          <li v-for="(file, index) in pendingCustomerFiles" :key="`${file.name}-${index}`">
            <span>{{ file.name }}</span>
            <small>{{ formatSize(file.size) }} · 待创建后上传</small>
            <button class="inline-button" type="button" @click="dropPendingCustomer(index)">移除</button>
          </li>
        </ul>
      </div>
      <!-- 客户 CR 号（0826 台账列）：客户侧的 CR/ECN/EWO 编号，台账拿它和客户对账 -->
      <label v-if="needsCustomerText" class="field">
        <span>客户 CR 号</span>
        <input
          v-model="raise.customerChangeNo" type="text"
          placeholder="客户侧的 CR / ECN / EWO 编号；客户没编号就留空"
        >
      </label>

      <!--
        0911 Tony：「按变更来源、客户的变更需求、变更内容顺序来填写」。
        原来「变更内容」摆在最上面，人得先写完我们要改什么，往下才看到
        「这变更打哪来」——填表顺序和想事情的顺序是反的。
        现在：先说来源，客户来的就贴原文，再写我们据此要改什么。
      -->
      <!--
        变更内容：程序 7.1 的出口条件里写着。0826 Tony 定死为必填——
        问题描述 / 原因分析 / 方案验证 / 变更前后（《变更管理表》1.2、1.3）
        要么写在这里，要么做成 PPT / 照片传附件，附件也同为必填。
      -->
      <label class="field">
        <span>变更内容<b class="req">*</b></span>
        <textarea
          v-model="raise.reason" rows="3"
          placeholder="现状是什么、要改成什么。第 2 步的影响评审要照着这段逐条看"
        ></textarea>
      </label>
      <!--
        0826 Tony：文字和附件**都必填**（0825 的二选一收回）。
        把附件数摆在眼前，让人在提交前就知道自己够不够格，而不是交上去才挨一个 400。
      -->
      <p v-if="!isCreate" class="note attach-note">
        已挂 {{ attachmentCount }} 个附件。变更内容的文字和附件都是必填——问题描述、原因分析、方案验证、变更前后对比，说不清的做成 PPT / 照片传在下面。
      </p>

      <!--
        变更内容配附件（0824 上传功能；0826 升为必填）。
        程序 7.1 的出口条件本来就要求「相关凭证」：图纸截图、客户邮件、检测报告——
        这些东西打字描述不清楚，也不该逼人转述。传的是这条变更的业务附件；
        0920 起抽屉底下那块不再重复列一遍，这里就是唯一的全量清单。
      -->
      <div
        v-if="!isCreate" class="field change-attach" :class="{ 'is-dragover': contentDragOver }"
        @dragover.prevent="onContentDragOver" @dragleave="onContentDragLeave" @drop.prevent="onContentDrop"
      >
        <span>变更内容附件<b class="req">*</b><em>方案 PPT、对比照片、图纸截图、客户邮件——至少传 1 个</em></span>
        <!--
          0911 Tony：「客户的变更需求上传的附件不要放在变更内容一起」。
          贴在客户要求那一格的截图原来混在这张清单里，六个方案 PPT 中间夹一张
          「客户要求-2026….png」，看的人分不出哪个是客户给的、哪个是我们做的。
          现在按归属分开：这里只列变更内容的，客户给的列在客户要求那一格下面。
        -->
        <ul v-if="contentAttachments.length" class="change-attach-list">
          <li v-for="file in contentAttachments" :key="file.id">
            <a :href="attachmentUrl(file.id)" target="_blank" rel="noopener">{{ file.fileName }}</a>
            <small>{{ formatSize(file.sizeBytes) }}<template v-if="file.uploadedBy"> · {{ file.uploadedBy }}</template></small>
            <button
              class="inline-button attach-remove"
              type="button"
              :disabled="file.canRemove === false || removingId === file.id"
              :title="file.removeBlockedReason || '把这份材料从这条变更上撤下来（不会真的删掉）'"
              @click="removeAttachment(file)"
            >{{ removingId === file.id ? '撤下中…' : '撤下' }}</button>
          </li>
        </ul>
        <p v-else-if="!attachLoading" class="change-attach-empty">还没有附件。</p>
        <div class="change-attach-actions">
          <input ref="attachInput" type="file" :disabled="uploading">
          <button class="button" type="button" :disabled="uploading" @click="uploadAttachment">
            {{ uploading ? '上传中…' : '上传附件' }}
          </button>
        </div>
        <!-- 能拖这件事要写出来：不写没人会去试（和客户要求那一格同一条规矩，0828） -->
        <small class="paste-note">文件也可以直接拖进这一块。</small>
      </div>
      <!-- 新建态：单子还不存在，文件先选好，创建时一并上传并提交第 1 步 -->
      <div
        v-else class="field change-attach" :class="{ 'is-dragover': contentDragOver }"
        @dragover.prevent="onContentDragOver" @dragleave="onContentDragLeave" @drop.prevent="onContentDrop"
      >
        <span>变更内容附件<b class="req">*</b><em>方案 PPT、对比照片、图纸截图、客户邮件——至少 1 个</em></span>
        <div class="change-attach-actions">
          <input ref="createFileRef" type="file" :disabled="props.creating" @change="onCreateFileChange">
        </div>
        <p class="note">在这里选好文件（也能直接把文件拖进来），提交时自动上传；手头还没有的也能先建草稿，之后回第 1 步补传再提交。</p>
      </div>

      <div class="field">
        <span>变更类型<b class="req">*</b></span>
        <div class="choices">
          <label v-for="option in typeOptions" :key="option.value" class="choice">
            <input v-model="raise.changeType" type="radio" :value="option.value"><i>{{ option.label }}</i>
          </label>
        </div>
        <!-- 细分项 Tony 还没给全，所以是可填可选：候选来自库里已经用过的写法 -->
        <input
          v-model="raise.changeSubType" list="change-subtype-options" type="text"
          placeholder="下级细分（可填可选，如 图纸尺寸 / 模具型腔）"
        >
        <datalist id="change-subtype-options">
          <option v-for="item in raiseForm.subTypeSuggestions" :key="item" :value="item" />
        </datalist>
        <!-- 两个大类各指什么（0826 Tony 给的定义原文）：模具改了算哪类，在这里点了名 -->
        <p v-if="raiseForm.typeNotes" class="note type-notes">
          <span v-for="(text, name) in raiseForm.typeNotes" :key="name"><b>{{ name }}</b>：{{ text }}</span>
        </p>
      </div>

      <div class="field">
        <span>发生阶段</span>
        <div v-for="group in stageGroups" :key="group.name" class="stage-row">
          <b>{{ group.name }}</b>
          <label v-for="option in group.options" :key="option" class="choice">
            <input v-model="raise.occurrenceStage" type="radio" :value="option"><i>{{ option }}</i>
          </label>
        </div>
      </div>

      <div class="grid">
        <label class="field">
          <span>产品类别</span>
          <input v-model="raise.productCategory" type="text" placeholder="按工艺大类，如 喷漆件 / 电镀件">
        </label>
        <label class="field">
          <span>变更零件号</span>
          <input v-model="raise.partNumber" type="text" placeholder="客户零件号，与 BOM 一致">
        </label>
        <label class="field">
          <span>变更产品名称</span>
          <input v-model="raise.productName" type="text" placeholder="如 出风口总成">
        </label>
        <label class="field">
          <span>关联问题单</span>
          <input v-model="raise.relatedIssue" type="text" placeholder="因某个问题单引起的，填那张单的编号">
        </label>
        <!--
          关联项目 / 变更牵头人：0826 通用字段区删掉后搬进来的。
          项目决定 ECN-项目号-001 编不编得出来（「待归属」的单子在这里补挂）；
          牵头人是变更管理者，自动判定规则没定之前由发起人手工指定。
          项目下拉只在父层给了选项时显示（项目页建单自动挂当前项目，不用选）。
        -->
        <label v-if="props.projectOptions?.length" class="field">
          <span>关联项目</span>
          <select v-model="raise.projectId">
            <option value="">留空 = 按标题里的项目代号自动挂</option>
            <option v-for="option in props.projectOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="field">
          <!-- 0911 Tony:「变更牵头人一定要填写」 -->
          <span>变更牵头人<b class="req">*</b></span>
          <input
            v-model="raise.ownerName" list="change-step-form-people" type="text"
            placeholder="谁牵头跟这条变更"
          >
          <datalist id="change-step-form-people">
            <option v-for="person in props.people ?? []" :key="person" :value="person" />
          </datalist>
        </label>
      </div>

      <p class="code-line">
        <template v-if="isCreate">
          提交第 1 步时自动生成变更编号 <b>ECN-项目号-001</b>（同项目内依次递增）；标题带上项目代号会自动挂到项目
        </template>
        <template v-else-if="raiseForm.code">变更编号 <b>{{ raiseForm.code }}</b>（已生成，不再变）</template>
        <template v-else-if="raiseForm.projectNo">
          提交时自动编号：<b>ECN-{{ raiseForm.projectNo }}-00X</b>，同项目内依次递增
        </template>
        <template v-else>这条变更还没挂项目，编不出 ECN-项目号-001；在上面「关联项目」里挂上再提交一次会补编号</template>
      </p>
      <!-- 手绘稿角落那句「自动判定变更管理者」这一版不做，照实说 -->
      <p class="note">{{ forms.managerNote }}</p>
    </template>

    <!-- ============ 第 2 步 ============ -->
    <template v-else-if="assessForm">
      <div v-for="group in assessForm.groups" :key="group.key" class="impact-group">
        <b>{{ group.name }}</b>
        <div class="choices">
          <!-- 「不适用」和同类别其它勾互斥（0826）：看过且不涉及 ≠ 还没看 -->
          <label v-for="item in group.items" :key="item.value" class="choice">
            <input
              type="checkbox" :checked="picked.includes(item.value)"
              @change="toggleImpact(group.items, item.value)"
            ><i>{{ item.label }}</i>
          </label>
        </div>
      </div>

      <!--
        可行性评估（0825 需求 2，《变更管理表》1.4 的表单化）：
        勾了 模具/工装/检具/设备 任一项才出现，且只列勾中的那几项。
        取消勾选时这一项从表单消失，但填过的值留在本地——勾回来还在，
        提交时也只送勾中的项（见 submit 里的说明）。
      -->
      <div v-if="feasibilityVisible.length" class="impact-group feasibility">
        <b>可行性评估（涉及工/检/模/设备）</b>
        <!-- 0826：责任人按项目团队自动带出（唯一对口才带，可改）；方案能直接传附件 -->
        <small v-if="Object.values(ownerSuggestions).some(Boolean)" class="feas-hint">
          责任人已按项目团队的对口工程师自动带出，不对就改。
        </small>
        <small class="feas-hint">
          选责任人<b>不会</b>自动发信——确认选对了再点「提醒他填写」，会存一次草稿并用站内信 + 邮件通知他。
        </small>
        <div v-for="item in feasibilityVisible" :key="item.key" class="feasibility-row">
          <!--
            0901 重排：原来是 4 列挤一行（标签/责任人/方案/成本），加了提醒按钮之后
            挤成 5 个、成本被顶到第二行、按钮孤零零落在右边——就是 Tony 说的"很乱"。
            现在一项一张卡：头一行放"是什么 + 谁负责 + 提醒他"，下面两行各占一整行。
          -->
          <div class="feas-head">
            <b>{{ item.label }}</b>
            <label class="field">
              <span>责任人</span>
              <!--
                0901 Tony：「做成下拉选择，名字打错的风险也没有了」。
                选项由后端给，只含派单解析得出来的人 —— 选了就一定提醒得到。
              -->
              <select v-model="feasibility[item.key].owner">
                <option value="">请选择（必填）</option>
                <!-- 库里存着但已不在名单里的旧值：留着，别一保存就把人抹了 -->
                <option v-if="legacyOwner(item.key)" :value="legacyOwner(item.key)">
                  {{ legacyOwner(item.key) }}（已不在名单里）
                </option>
                <optgroup v-if="ownerOptions.team.length" label="本项目团队">
                  <option v-for="person in ownerOptions.team" :key="person" :value="person">{{ person }}</option>
                </optgroup>
                <optgroup :label="ownerOptions.team.length ? '其他同事' : '全部同事'">
                  <option v-for="person in ownerOptions.others" :key="person" :value="person">{{ person }}</option>
                </optgroup>
              </select>
            </label>
            <button
              v-if="props.changeId !== null"
              type="button" class="button ghost feas-notify"
              :disabled="busy || notifyingKey !== null || !feasibility[item.key].owner.trim()"
              :title="feasibility[item.key].owner.trim()
                ? `保存并用站内信 + 邮件提醒 ${feasibility[item.key].owner.trim()} 来填${item.label}方案`
                : '先选责任人'"
              @click="notifyOwner(item.key, item.label)"
            >{{ notifyingKey === item.key ? '发送中…' : '提醒他填写' }}</button>
          </div>
          <label class="field">
            <span>方案提报</span>
            <span class="input-attach">
              <input v-model="feasibility[item.key].note" type="text" placeholder="可行性结论与方案（必填）；文件点 📎 传">
              <button
                v-if="props.changeId !== null"
                type="button" class="attach-inline"
                :disabled="feasUploadingKey !== null"
                :title="`上传${item.label}方案附件（PPT / 照片 / 图纸）`"
                @click="pickFeasibilityFile(item.key)"
              >{{ feasUploadingKey === item.key ? '…' : '📎' }}</button>
            </span>
            <small v-if="feasibilityFiles(item.label).length" class="feas-files">
              <a
                v-for="file in feasibilityFiles(item.label)" :key="file.id"
                :href="attachmentUrl(file.id)" target="_blank" rel="noopener"
              >{{ file.fileName }}</a>
            </small>
          </label>
          <!-- 预估成本估算（0826 Tony）：选填；金额存文本不做算术（全库口径） -->
          <label class="field">
            <span>预估成本估算</span>
            <input
              v-model="feasibility[item.key].cost" type="text"
              :placeholder="`如 ${item.label}改造约 2 万；存文本不做算术`"
            >
          </label>
        </div>
        <input ref="feasFileInput" type="file" class="feas-hidden-file" @change="uploadFeasibilityFile">
      </div>

      <!--
        是否影响其他产品（0825 需求 3）：共用件、共模件改了会牵连别的项目，
        评审时必须给个明话。选「有」才追问怎么处理和预估费用；
        费用存文本不做算术（全库口径）。老单子没填过就显示未选，不强迫补。
      -->
      <div class="field affects-other">
        <span>是否影响其他产品</span>
        <div class="choices">
          <label class="choice"><input v-model="affectsOtherProducts" type="radio" value="有"><i>有</i></label>
          <label class="choice"><input v-model="affectsOtherProducts" type="radio" value="无"><i>无</i></label>
        </div>
      </div>
      <div v-if="affectsOtherProducts === '有'" class="grid affects-other-detail">
        <label class="field">
          <span>影响的产品如何处理（必填）</span>
          <textarea
            v-model="affectedProductsPlan" rows="2"
            placeholder="受影响的产品/项目怎么同步处理——不写这格，第 2 步交不上去"
          ></textarea>
        </label>
        <label class="field">
          <span>预估费用（选填）</span>
          <input v-model="affectedProductsCost" type="text" placeholder="如 模具改造约 2 万；存文本，不做算术">
        </label>
      </div>

      <div class="grid">
        <label class="field">
          <span>风险等级</span>
          <select v-model="riskLevel">
            <option value="">请选择</option>
            <option v-for="level in assessForm.riskLevels" :key="level" :value="level">{{ level }}</option>
          </select>
        </label>
        <label class="field">
          <span>变更等级</span>
          <select v-model="changeLevel">
            <option value="">请选择</option>
            <option v-for="level in assessForm.changeLevels" :key="level" :value="level">{{ level }}</option>
          </select>
        </label>
      </div>
      <p class="note">
        A 重大须客户书面批准；B 一般实施前告知客户；C 轻微内部备案（客户发起的变更不用再批准 / 告知，A 级以第 1 步的客户要求原文为授权）。等级决定第 3 步走哪条审批路径。
      </p>
      <p v-if="assessForm.values.impactScopes" class="note">
        现有影响范围（执行清单按它裁）：{{ assessForm.values.impactScopes }} —— 勾选只会往里加，不会减。
      </p>
      <!--
        以前手打进 impactItems 的写法上面勾不出来。**不能当没看见**：
        塞回勾选里是个取消不掉的隐形勾（提交时被当野值退回，这一步就永远交不上），
        当没有则等于无声删掉别人填的。所以照实列出来，说明提交后它还在。
      -->
      <p v-if="assessForm.values.legacyImpactItems?.length" class="note legacy">
        以前填过、上面勾选里没有的写法：{{ assessForm.values.legacyImpactItems.join('、') }}
        —— 提交后照样留着，不会被抹掉；要改就去变更表单的「影响评审勾选项」那一格。
      </p>
    </template>
    </fieldset>

    <!--
      还差什么，摆在按钮上面（0901 Tony：「只要有没填写的这第二步就不能提交，不然整个不完整」）。
      门禁本身 0829 就有了，但它只在**点提交那一刻**说话 ——
      门禁之前交上来的单子（商务成本、客户合规两整类全空）表单任务早就勾掉了，
      第 2 步一直显示「已提交」，没人知道它其实缺着。现在照实摆出来。
    -->
    <p v-if="assessForm && assessForm.gaps?.length" class="form-gaps">
      <b>这一步还差 {{ assessForm.gaps.length }} 项，补齐才能提交</b>
      <span v-for="(gap, at) in assessForm.gaps" :key="at">{{ gap }}</span>
    </p>

    <!-- 只能看的时候不摆保存 / 提交：为什么只能看，五步条顶上那条横幅说了 -->
    <div v-if="!locked" class="form-actions">
      <button
        v-if="!isCreate && !isRaise"
        class="button ghost" type="button" :disabled="busy || props.creating" @click="saveDraft"
      >{{ busy ? '保存中…' : '保存草稿' }}</button>
      <button class="button" type="button" :disabled="busy || props.creating" @click="submit">
        <template v-if="isCreate">{{ props.creating ? '创建中…' : (createFilePicked ? '创建并提交第 1 步' : '创建（存为草稿）') }}</template>
        <template v-else>{{ busy ? '提交中…' : '提交 / 下一步' }}</template>
      </button>
      <small v-if="isCreate">选了附件 = 创建后自动上传并提交第 1 步；没选 = 先存草稿，附件补上再提交。</small>
      <!-- 0826：正停在这一步时提交完自动推进；老单子回头改表单不动进度 -->
      <small v-else-if="isRaise">提交 = 交上这一步的必填项；正停在这一步时提交完会自动推进到下一步。</small>
      <small v-else>
        保存草稿 = 存进库里，别人打开看得见，不判必填也不推进（这张表本来就不止一个人填）；
        填了可行性责任人还会顺手提醒他来写方案。提交 = 交上必填项，齐了就自动推进到下一步。
      </small>
    </div>
  </section>
  <!--
    读不到就说一句，别让表单无声消失。
    上面那条「填写并提交《变更发起申请》」的任务勾不掉也删不掉，
    表单又不见了的话，人只会看见一个永远过不去的步骤，还不知道为什么。
  -->
  <p v-else-if="loadFailed" class="form-broken">
    这一步的表单没读出来，先刷新一下页面。这一步的必填项只能在这张表里提交——
    一直读不到就把变更编号发给系统管理员。
  </p>
</template>

<style scoped>
.feas-notify { justify-self: start; white-space: nowrap; }
.form-gaps {
  display: grid; gap: 4px; margin: 0; padding: 10px 12px;
  border: 1px solid var(--risk, #d93025); border-radius: 8px;
  background: color-mix(in srgb, var(--risk, #d93025) 6%, transparent);
  font-size: 12px; line-height: 1.6;
}
.form-gaps b { color: var(--risk, #d93025); }
.change-step-form { display: grid; gap: 10px; margin: 10px 0 4px; padding: 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--white); }
/* 只为一次灰掉整张表才包的这一层：去掉 fieldset 自带的框和缩进，排版照旧 */
.form-body { display: grid; gap: 10px; min-width: 0; margin: 0; padding: 0; border: 0; }
.form-head { display: flex; align-items: baseline; gap: 8px; margin: 0; }
.form-head em { font-style: normal; font-size: 11px; }
.form-head .ok { color: #2e9e6b; }
.form-head .todo { color: #b8860b; }
.field { display: grid; gap: 4px; font-size: 12px; }
.field > span { color: var(--muted); font-size: 11px; }
/* 必填星号：和 RecordFormDialog 的必填标记同一个红 */
.field > span .req { margin-left: 2px; color: #c0392b; }
.grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
.choices { display: flex; flex-wrap: wrap; gap: 6px; }
.choice { display: inline-flex; align-items: center; gap: 5px; padding: 4px 9px; border: 1px solid var(--line); border-radius: 999px; background: var(--surface-2, #f7f9fc); cursor: pointer; }
.choice i { font-style: normal; font-size: 12px; }
.stage-row { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.stage-row > b { min-width: 48px; font-size: 11px; color: var(--muted); }
.impact-group { display: grid; gap: 5px; padding: 8px 10px; border: 1px solid var(--line); border-radius: 8px; background: var(--surface-2, #f7f9fc); }
.impact-group > b { font-size: 12px; }
/* 可行性评估（0825）：一行一个涉及项，项名 + 责任人 + 方案提报 + 预估成本（0826） */
.feasibility-row {
  display: grid; gap: 8px; padding: 10px;
  border: 1px solid var(--line); border-radius: 8px;
  background: var(--white);
}
.feas-head { display: grid; grid-template-columns: 48px minmax(150px, 1fr) auto; gap: 8px; align-items: end; }
.feas-head > b { padding-bottom: 6px; font-size: 12px; }
.feasibility-row select { width: 100%; }
.feas-hint { color: var(--muted); font-size: 11px; }
/* 方案框内的上传按钮（0826）：按钮浮在输入框右端，框留出右内边距 */
.input-attach { position: relative; display: block; }
.input-attach input { width: 100%; padding-right: 30px; }
.attach-inline {
  position: absolute; top: 50%; right: 4px; transform: translateY(-50%);
  padding: 1px 5px; border: 1px solid var(--line); border-radius: 6px;
  background: var(--surface-2, #f7f9fc); font-size: 12px; line-height: 1.4; cursor: pointer;
}
.attach-inline:hover { border-color: var(--blue); }
.feas-files { display: flex; flex-wrap: wrap; gap: 8px; font-size: 11px; }
.feas-hidden-file { display: none; }
/* 变更类型的两条定义（0826）：一类一行，别挤成一段 */
.type-notes span { display: block; }
@media (max-width: 640px) {
  .feas-head { grid-template-columns: 1fr; align-items: stretch; }
  .feas-head > b { padding-bottom: 0; }
  .feas-notify { justify-self: stretch; }
}
.code-line { margin: 0; font-size: 12px; }
.note { margin: 0; color: var(--muted); font-size: 11px; line-height: 1.5; }
.note.legacy { color: #b8860b; }
.form-broken { margin: 10px 0 4px; padding: 10px 12px; border: 1px solid rgba(184, 134, 11, .45); border-radius: 10px; background: rgba(184, 134, 11, .07); color: #8a6508; font-size: 12px; line-height: 1.6; }
.form-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.form-actions small { color: var(--muted); font-size: 11px; }

/* 拖到这一块上时给个明确反馈，否则人不知道松手会不会生效 */
.change-attach.is-dragover,
.customer-req.is-dragover { outline: 2px dashed var(--blue); outline-offset: 2px; border-radius: 8px; }
/* 客户要求这一格整块都是拖放区：文本框、按钮、已挂清单都在里面 */
.customer-req { display: block; }
.customer-req > label { display: block; }
.customer-req .change-attach-actions { margin-top: 6px; }
.customer-req .field-hint { color: var(--muted); font-size: 11px; font-style: normal; }
/* 客户那一格底下的附件清单：缩一号，和变更内容附件区分开 */
.customer-attach { margin-top: 6px; }

/* 变更内容的附件（0824）：紧跟在变更内容下面，视觉上属于同一件事 */
.change-attach span em { margin-left: 6px; color: var(--muted); font-style: normal; font-weight: 400; }
/* 撤下按钮跟在文件名后面，别抢主位：主动作是打开文件 */
.change-attach-list .attach-remove {
  margin-left: 6px;
  color: #b45309;
  border-color: #fde68a;
}
.change-attach-list .attach-remove:disabled { color: #94a3b8; border-color: #e2e8f0; }

.change-attach-list { display: grid; gap: 3px; margin: 0 0 6px; padding: 0; list-style: none; }
.change-attach-list li { display: flex; align-items: baseline; gap: 8px; }
.change-attach-list small { color: var(--muted); font-size: 11px; }
.change-attach-empty { margin: 0 0 6px; color: var(--muted); }
.change-attach-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
/* 贴图那一格：正在上传时给个明确的态，别让人以为没反应 */
.field textarea.pasting { border-color: #93c5fd; background: #f8fbff; }
.paste-note { display: block; margin-top: 4px; color: var(--muted); font-size: 11px; line-height: 1.6; }

</style>
